import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect, useRef, useState } from "react";
import { checkToken } from "../../store/auth-slice";
import { logout } from "../../store/auth-slice";
import { onMessage } from "firebase/messaging";
import { messaging } from "../../firebase"; // your firebase.js
import { getToken } from "firebase/messaging";
import UiContext from "../../store/ui-context";
import { useAPISaveDeviceToken } from "./api/api-save-device-token";

let didInit = false;

const ProtectedRoute = ({ children }) => {
  const dispatch = useDispatch();
  const uiContext = useContext(UiContext);

  const user = useSelector((state) => state.auth.user);
  const permissions = useSelector((state) => state.auth.permissions);
  const [hasSentFCM, setHasSentFCM] = useState(false);

  const onConfirm = (row) => {
    setHasSentFCM(true);
  };

  const { loading, saveData } = useAPISaveDeviceToken({ onConfirm });

  // ===== 🔊 AUDIO SETUP (play /notif.mp3 on foreground messages) =====
  const audioRef = useRef(null);
  const [audioReady, setAudioReady] = useState(false);
  const lastPlayRef = useRef(0);

  // Create audio element once
  useEffect(() => {
    audioRef.current = new Audio("/notif.mp3"); // place file in /public
    audioRef.current.preload = "auto";
    audioRef.current.volume = 1.0;
  }, []);

  // Unlock audio on first user gesture (required by autoplay policies)
  useEffect(() => {
    const unlock = async () => {
      try {
        await audioRef.current.play();
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setAudioReady(true);
      } catch {
        /* ignore — will try again on next gesture if needed */
      } finally {
        window.removeEventListener("click", unlock);
        window.removeEventListener("touchstart", unlock);
        window.removeEventListener("keydown", unlock);
      }
    };
    window.addEventListener("click", unlock, { once: true, passive: true });
    window.addEventListener("touchstart", unlock, { once: true, passive: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Safe play helper (only when tab visible; throttle duplicates)
  const playNotifSound = async () => {
    if (!audioRef.current || !audioReady) return;
    if (document.visibilityState !== "visible") return; // foreground only
    const now = Date.now();
    if (now - lastPlayRef.current < 600) return; // 0.6s throttle
    lastPlayRef.current = now;

    try {
      audioRef.current.currentTime = 0;
      await audioRef.current.play();
    } catch (err) {
      console.warn("Audio play failed:", err);
    }
  };
  // ===== END AUDIO SETUP =====

  useEffect(() => {
    if (!didInit) {
      didInit = true;

      if (user) {
        dispatch(checkToken());
      }
    }
  }, []);

  useEffect(() => {
    if (hasSentFCM) {
      const unsubscribe = onMessage(messaging, (payload) => {
        // console.log("📩 Received foreground message:", payload);

        // 🔔 Handle visual alert ONLY when app is in foreground
        // This prevents duplicate notifications since the service worker
        // already handles background notifications
        const { title, body } = payload.data || {};
        const { type } = payload.data || {};

        // Only show in-app notification when the app is in foreground
        uiContext.showSuccessMsg(type.toLocaleUpperCase(), `${title}: ${body}`);

        // 🔊 Play sound here
        playNotifSound();

        // if (type === "notification") {
        //   // Example: store notification in Redux or state
        //   // Or route to specific notification window
        //   console.log("Open notification:", payload.data);
        // }

        // You can also use modal/pop-up, play audio, etc.
      });

      return () => unsubscribe(); // Clean up on unmount
    }
  }, [hasSentFCM]);

  useEffect(() => {
    // Once logged in and FCM not sent yet
    if (user && !hasSentFCM) {
      const sendFCMToken = async () => {
        try {
          const vapidKey = process.env.REACT_APP_FCM_VAPID_PUBLIC_KEY; // get from Firebase Console
          const fcmToken = await getToken(messaging, { vapidKey });

          if (fcmToken) {
            saveData({
              token: fcmToken,
              platform: "web",
              browser: navigator.userAgent.includes("Firefox")
                ? "firefox"
                : "chrome",
              user_agent: navigator.userAgent,
              is_active: 1,
            });
            setHasSentFCM(true); // Make sure to set this flag
          } else {
            console.warn("No FCM token available.");
          }
        } catch (err) {
          console.error("Error fetching FCM token:", err);
        }
      };

      sendFCMToken();
    }
  }, [user, hasSentFCM]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!permissions.includes("view adminpage")) {
    dispatch(logout());
    return <Navigate to="/login" />;
  }

  return children;
};

export default ProtectedRoute;
